import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { useUrlState } from '../nav/urlState';
import { trapTab } from '../ui/focus';
import { DeleteManualDialog, ManualReceivableDialog } from './ManualReceivableDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { REVIEW_KEY, ReviewPage } from './ReviewPage';
import { ToConfirmPage } from './ToConfirmPage';
import { formText, METHODS, PAYMENTS_KEY, RECEIVABLES_RESOURCE, stateLabel, type Receivable, type ReviewItem } from './types';
import './payments.css';
import { DialogLayer } from '../ui/Dialog';
import { RowMenu } from '../ui/RowMenu';

type StateFilter = 'otwarte' | 'zalegle' | 'wszystkie';
const STATES: Array<{ id: StateFilter; label: string }> = [
  { id: 'otwarte', label: 'Otwarte' },
  { id: 'zalegle', label: 'Zaległe' },
  { id: 'wszystkie', label: 'Wszystkie' },
];
const money = (g: number | undefined) => (g === undefined ? '—' : formatPln(g));

/** "Wyślij przypomnienie": the e-mail from the template in the settings, to check and change before sending. */
function RemindDialog({ receivable, onClose }: { receivable: Receivable; onClose: () => void }) {
  const preview = useQuery({ queryKey: [...PAYMENTS_KEY, 'remind', receivable.id], queryFn: () => api<{ to: string; subject: string; body: string }>(`/api/payments/receivables/${receivable.id}/remind-preview`) });
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  useEffect(() => {
    if (preview.data) {
      setTo(preview.data.to);
      setSubject(preview.data.subject);
      setBody(preview.data.body);
    }
  }, [preview.data]);
  const send = useMutation({ mutationFn: () => api<{ queued: boolean }>(`/api/payments/receivables/${receivable.id}/remind`, { method: 'POST', body: { to, subject, body } }) });
  function submit(e: FormEvent) {
    e.preventDefault();
    send.mutate();
  }
  const error = send.error instanceof ApiError ? (fieldIssues(send.error)[0]?.message ?? send.error.message) : send.error ? 'Nie udało się wysłać' : '';
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel form pay-remind"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remind-title"
        data-feature-id="payments.receivables"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="remind-title">Przypomnienie o płatności</h2>
        {preview.error && <p className="alert" role="alert">{preview.error instanceof ApiError ? preview.error.message : 'Nie udało się przygotować e-maila'}</p>}
        {send.data ? (
          <p role="status">{send.data.queued ? `Wysłano przypomnienie na ${to}.` : 'Przypomnienie do tej należności zostało już dziś wysłane.'}</p>
        ) : (
          <>
            <label className="field">
              <span>Do</span>
              <input type="email" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="field">
              <span>Temat</span>
              <input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
            </label>
            <label className="field">
              <span>Treść</span>
              <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
          </>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          {!send.data && (
            <button className="btn primary" type="submit" disabled={send.isPending || !preview.data}>
              Wyślij
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            {send.data ? 'Zamknij' : 'Anuluj'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * "Finanse → Płatności": "Należności" (F36) — open ones with the overdue first, overdue only, or all —, "Do
 * potwierdzenia" (owner 2026-10-22: confirming payments by hand) and, for whoever approves matches, "Do zatwierdzenia"
 * (F38) with the number of operations waiting.
 */
export function ReceivablesPage() {
  const { me } = useMe();
  const { params, update } = useUrlState();
  const approver = !!me && can(me, RECEIVABLES_RESOURCE, 'approve');
  const review = useQuery({ queryKey: REVIEW_KEY, queryFn: () => api<{ items: ReviewItem[] }>('/api/payments/review'), enabled: approver });
  if (!me) return null;
  const reviewing = approver && params.get('widok') === 'do-zatwierdzenia';
  const confirming = params.get('widok') === 'do-potwierdzenia';
  const listing = !reviewing && !confirming;
  return (
    <section className="page wide pay-page" aria-label="Płatności" data-feature-id={reviewing ? 'payments.review' : confirming ? 'payments.confirm' : 'payments.receivables'}>
      <div className="segment pay-views" role="group" aria-label="Widok płatności">
        <button type="button" aria-pressed={listing} onClick={() => update({ widok: null, forma: null, 'po-terminie': null })}>
          Należności
        </button>
        <button type="button" aria-pressed={confirming} onClick={() => update({ widok: 'do-potwierdzenia', stan: null, q: null })}>
          Do potwierdzenia
        </button>
        {approver && (
          <button type="button" aria-pressed={reviewing} onClick={() => update({ widok: 'do-zatwierdzenia', stan: null, q: null, forma: null, 'po-terminie': null })}>
            Do zatwierdzenia
            {review.data && review.data.items.length > 0 && <span className="pay-segment-count">({review.data.items.length})</span>}
          </button>
        )}
      </div>
      {reviewing ? <ReviewPage /> : confirming ? <ToConfirmPage /> : <ReceivablesList />}
    </section>
  );
}

function ReceivablesList() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const state = (['otwarte', 'zalegle', 'wszystkie'] as const).find((s) => s === params.get('stan')) ?? 'otwarte';
  const q = params.get('q') ?? '';
  const [paying, setPaying] = useState<Receivable | null>(null);
  const [reminding, setReminding] = useState<Receivable | null>(null);
  // „+ Należność” (owner 2026-11-03): `true` a new one, a row: its change
  const [manual, setManual] = useState<Receivable | true | null>(null);
  const [deleting, setDeleting] = useState<Receivable | null>(null);
  const search = new URLSearchParams({ state, ...(q.trim() ? { q: q.trim() } : {}) });
  const list = useQuery({ queryKey: [...PAYMENTS_KEY, 'list', state, q.trim()], queryFn: () => api<{ receivables: Receivable[] }>(`/api/payments/receivables?${search.toString()}`) });
  if (!me) return null;
  const rows = list.data?.receivables ?? [];
  const amounts = fieldVisible(me, RECEIVABLES_RESOURCE, 'remainingGrosze');
  const recording = can(me, RECEIVABLES_RESOURCE, 'create') && fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  const remindable = can(me, RECEIVABLES_RESOURCE, 'remind') && amounts;
  // adding and changing one carries its amount (rule 7a): the plan's right and the amount field
  const manualEditable = can(me, RECEIVABLES_RESOURCE, 'edit') && fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  return (
    <>
      <div className="pay-filters">
        <div className="segment" role="group" aria-label="Które należności">
          {STATES.map((s) => (
            <button key={s.id} type="button" aria-pressed={state === s.id} onClick={() => update({ stan: s.id === 'otwarte' ? null : s.id })}>
              {s.label}
            </button>
          ))}
        </div>
        <input className="pay-search" type="search" aria-label="Szukaj należności" placeholder="Klient, wydarzenie, numer faktury" value={q} onChange={(e) => update({ q: e.target.value || null })} />
        {manualEditable && (
          <button className="btn primary" type="button" data-feature-id="payments.manual" onClick={() => setManual(true)}>
            + Należność
          </button>
        )}
      </div>
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać należności'}
        </p>
      )}
      {list.data && rows.length === 0 && <p className="muted">{state === 'zalegle' ? 'Brak zaległych należności.' : 'Brak należności. Powstają z planu płatności wydarzenia (zakładka „Płatności”), z wystawionych faktur albo z „+ Należność”.'}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table pay-table" aria-label="Należności">
            <thead>
              <tr>
                <th scope="col">Należność</th>
                <th scope="col">Klient</th>
                <th scope="col" className="wide-only">
                  Wydarzenie
                </th>
                <th scope="col">Termin</th>
                <th scope="col" className="wide-only">
                  Forma
                </th>
                {amounts && (
                  <th scope="col" className="num">
                    Pozostało
                  </th>
                )}
                <th scope="col">Stan</th>
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">
                    {r.label}
                    {r.source === 'manual' && <span className="pay-tag">ręczna</span>}
                  </td>
                  <td>{r.contractorName || <span className="muted">bez klienta</span>}</td>
                  <td className="wide-only">{r.eventId ? <Link to={`/wydarzenia?wydarzenie=${r.eventId}&zakladka=platnosci`}>{r.eventName}</Link> : null}</td>
                  <td className="nowrap">{formatDayText(r.dueDate)}</td>
                  <td className="wide-only">{formText(r) || METHODS[r.method]}</td>
                  {amounts && <td className="num nowrap">{money(r.remainingGrosze)}</td>}
                  <td>
                    <span className="pay-state" data-state={r.overdueDays > 0 ? 'zalegla' : r.state}>
                      {stateLabel(r)}
                    </span>
                  </td>
                  <td className="q-actions">
                    {recording && r.state === 'otwarta' && (
                      <button className="btn ghost small" type="button" onClick={() => setPaying(r)}>
                        Odnotuj wpłatę
                      </button>
                    )}
                    {remindable && r.state === 'otwarta' && r.overdueDays > 0 && (
                      <button className="btn ghost small" type="button" onClick={() => setReminding(r)}>
                        Przypomnij
                      </button>
                    )}
                    {manualEditable && r.source === 'manual' && r.state !== 'anulowana' && (
                      <RowMenu
                        label={`Więcej działań: ${r.label}`}
                        actions={[
                          { label: 'Zmień', onSelect: () => setManual(r) },
                          { label: 'Usuń', danger: true, onSelect: () => setDeleting(r) },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paying && (
        <RecordPaymentDialog
          receivable={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
          }}
        />
      )}
      {reminding && <RemindDialog receivable={reminding} onClose={() => setReminding(null)} />}
      {manual && <ManualReceivableDialog {...(manual === true ? {} : { receivable: manual })} onClose={() => setManual(null)} />}
      {deleting && <DeleteManualDialog receivable={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}
